import * as React from 'react'
import styled, { css } from 'styled-components'
import { ColorName, Colors, Fonts } from '../constants'

const inlineBlock = 'inline-block'

export const H1 = styled.h1`
  ${(props: { color?: ColorName }) => css`
    font-family: ${Fonts.sansHeader};
    font-size: 32px;
    font-weight: normal;
    line-height: 1.5;
    color: ${Colors[props.color || 'white']};
  `};
`

export const H2 = styled.h2`
  ${(props: { color?: ColorName }) => css`
    font-size: 24px;
    font-family: ${Fonts.sansHeader};
    font-weight: normal;
    line-height: 1.33;
    color: ${Colors[props.color || 'white']};
  `};
`

export const H3 = styled.h3`
  ${(props: { color?: ColorName }) => css`
    font-size: 21px;
    font-family: ${Fonts.sansHeader};
    font-weight: normal;
    color: ${Colors[props.color || 'white']};
    line-height: 27px;
  `};
`

export const H4 = styled.h4`
  ${(props: { color?: ColorName }) => css`
    font-size: 16px;
    font-family: ${Fonts.sansHeader};
    font-weight: normal;
    color: ${Colors[props.color || 'white']};
    text-transform: uppercase;
    letter-spacing: 1.6px;
  `};
`

export const H5 = styled.h5`
  ${(props: { color?: ColorName }) => css`
    font-size: 16px;
    font-family: ${Fonts.sansHeader};
    font-weight: normal;
    color: ${Colors[props.color || 'silver']};
    line-height: 1.5;
    margin: 0;
  `};
`

export const IntroText = styled.div`
  ${(props: { color?: ColorName }) => css`
    font-size: 16px;
    font-family: ${Fonts.sansBody};
    line-height: 1.62;
    color: ${Colors[props.color || 'silver']};
  `};
`

export const IntroTextSpan = IntroText.withComponent('span')

export const BodyText = styled.div`
  ${(props: { color?: ColorName }) => css`
    font-size: 14px;
    font-family: ${Fonts.sansBody};
    line-height: 1.5;
    color: ${Colors[props.color || 'midway']};
    font-feature-settings: inherit; /* if parent has font-features, inherit them */
  `};
`

export const BodyTextSpan = BodyText.withComponent('span')

export const BodyMonoText = styled(BodyText)`
  font-family: ${Fonts.mono};
  white-space: pre-wrap;
`

/**
 * Deprecated: both `withComponent()` and `extend()`
 * instead, use: `<BodyMonoText as="span" .../>`
 * see: https://github.com/dynasty-com/lisa/pull/5121#discussion_r317311654
 */
export const BodyMonoTextSpan = BodyMonoText.withComponent('span')

export const SmallerText = styled.div`
  ${(props: { color?: ColorName; align?: string }) => css`
    font-size: 12px;
    font-family: ${Fonts.sansBody};
    line-height: 1.25;
    color: ${Colors[props.color || 'midway']};
    text-align: ${props.align || 'left'};
  `};
`

export const SmallerTextSpan = SmallerText.withComponent('span')

export const SmallerMonoText = styled(SmallerText)`
  font-family: ${Fonts.mono};
`

export const SmallerMonoTextSpan = SmallerMonoText.withComponent('span')

export const ErrorText = styled(SmallerText)`
  margin-top: 17px;
  color: ${Colors.coral};
`
export const ErrorTextSpan = ErrorText.withComponent('span')

export const ColoredSpan = styled.span`
  ${(props: { color?: ColorName }) => css`
    color: ${Colors[props.color || 'midway']};
  `};
`

export const AlignRight = styled.div`
  text-align: right;
`

export const FixedWidthSpan = styled.span`
  ${(props: { width: number; color?: ColorName; center?: boolean }) => css`
    display: inline-block;
    width: ${props.width}px;
    ${props.color && `color: ${Colors[props.color]};`}
    ${props.center && 'text-align: center;'}
  `};
`

export const ClickableSpan = styled.span`
  cursor: default;
  user-select: all;
`

/**
 * Default <sup> alters line height.
 *
 * This is a close visual match, but does not affect the height of the containing line.
 */
export const Superscript = styled.sup`
  vertical-align: top;
  position: relative;
  top: -0.1em;
`

export const TextTag = styled.span`
  ${(props: { color: ColorName }) => css`
    line-height: 0.71;
    height: 22px;
    box-sizing: border-box;
    display: inline-block;
    letter-spacing: 0.5px;
    font-size: 14px;
    font-family: ${Fonts.sansBody};
    padding: 6px;
    text-transform: uppercase;
    border-radius: 2px;
    border: solid 1px transparent;
    border-color: ${Colors[props.color]};
    color: ${Colors[props.color]};
  `};
`

export const TextListItem = styled.span`
  display: inline-block;
  margin: 0 16px 8px 0;
`

export interface SpacerProps {
  width?: 4 | 8 | 12 | 16 | 24 | 32 | 48 | 64 | 96
  height?: 4 | 8 | 12 | 16 | 24 | 32 | 48 | 64 | 96
  grow?: boolean
}

export const Spacer = styled.div`
  ${(props: SpacerProps) => css`
    display: ${props.width ? inlineBlock : 'block'};
    height: ${props.height ? props.height + 'px' : 'auto'};
    width: ${props.width ? props.width + 'px' : 'auto'};
    margin: ${props.grow ? 'auto' : '0'};
  `};
`

const InputBase = styled.input`
  ${(props: { hasError?: boolean; hasSuccess?: boolean }) => css`
    box-sizing: border-box;
    background: none;
    color: ${Colors.white};
    font-family: ${Fonts.sansBody};
    border: ${'1px solid ' + (props.hasError ? Colors.coral : props.hasSuccess ? Colors.mint : Colors.charcoal)};
    &:focus {
      border: ${'1px solid ' + (props.hasError ? Colors.coral : props.hasSuccess ? Colors.mint : Colors.charcoal)};
    }
  `};
`

export const InputLarge = styled(InputBase)`
  width: 100%;
  height: 54px;
  padding: 4px 0 0 24px;
  font-size: 18px;
  border-radius: 4px;
`

export const InputMed = styled(InputBase)`
  height: 40px;
  padding: 2px 0 0 12px;
  font-size: 16px;
  border-radius: 2px;
`

export const InputInline = styled.input`
  ${(props: { hasError?: boolean }) => css`
    box-sizing: border-box;
    background: none;
    color: ${Colors.white};
    font-family: ${Fonts.sansBody};
    font-size: 12px;
    line-height: 1.25;
    border-bottom: ${'1px solid ' + (props.hasError ? Colors.coral : Colors.charcoal)};
    &:focus {
      border-bottom: ${'1px solid ' + (props.hasError ? Colors.coral : Colors.sky)};
    }
  `};
`

export const TextArea = styled.textarea`
  ${(props: { hasError?: boolean; disabled?: boolean; monospace?: boolean }) => css`
    padding: 12px;
    font-family: ${props.monospace ? Fonts.mono : Fonts.sansBody};
    font-size: 16px;
    line-height: 1.5;
    color: white;
    box-sizing: border-box;
    border: solid 1px ${props.hasError ? Colors.coral : Colors.charcoal};
    background: ${props.disabled ? Colors.charcoal : 'transparent'};
    overflow: hidden;
    outline-color: transparent;
    box-shadow: none;
    resize: vertical;
    &:focus,
    &:active {
      border: solid 1px ${props.hasError ? Colors.coral : props.disabled ? Colors.charcoal : Colors.sky};
    }
  `};
`

export const EllipsisSpan = styled.span`
  ${(props: { width?: string; maxWidth?: string }) => css`
    display: inline-block;
    width: ${props.width || 'auto'};
    max-width: ${props.maxWidth || props.width || '8em'};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    vertical-align: bottom;
  `}
`

export const EllipsisDiv = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: currentcolor;
`

export const NoWrapSpan = styled.span`
  white-space: nowrap;
`

export function Validator({ errorMessage, children }: { errorMessage?: string | null; children: React.ReactNode }) {
  return (
    <ValidatorWrap>
      {errorMessage && <ValidatorErrorDiv color="coral">{errorMessage}</ValidatorErrorDiv>}
      {children}
    </ValidatorWrap>
  )
}

const ValidatorWrap = styled.div`
  display: inline-block;
  position: relative;
`

const ValidatorErrorDiv = styled(BodyText)`
  position: absolute;
  top: 100%;
  white-space: pre;
`

export const DownArrow = ({ isDown }: { isDown: boolean }) => (
  <BodyTextSpan>
    <div
      style={{
        userSelect: 'none',
        display: inlineBlock,
        transform: `rotate(${isDown ? '-90' : '90'}deg)`,
        fontWeight: 'bold',
      }}
    >
      ›
    </div>
  </BodyTextSpan>
)

/**
 * Centers children horizontally and vertically.
 */
export const CenterFlexbox = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

export type WithScrollIntoViewIfNeeded<T> = T & { scrollIntoViewIfNeeded?: (opt_center?: boolean) => void }

export const TypeToggle = styled.div`
  ${(props: { isSelected: boolean; isAvailable?: boolean }) => css`
    transition: all 0.05s linear 0s;
    position: relative;
    display: inline-block;
    overflow: hidden;
    user-select: none;
    margin: 0 4px 4px 0;
    padding: 6px 8px;
    border-radius: 4px;
    border: 1px solid transparent;
    border-color: ${props.isSelected ? Colors.midway : Colors.ink};
    background-color: ${props.isSelected ? Colors.ink : Colors.charcoal};
    font: 12px ${Fonts.sansBody};
    color: ${Colors.silver};
    text-shadow: 0px 2px black;
    cursor: pointer;
    &:hover {
      /* hover styling generalizes well to all colors */
      filter: ${props.isAvailable ? 'brightness(150%)' : 'brightness(120%)'};
    }
  `};
`

export const TypeToggleInner = styled.div`
  ${(props: { isSelected?: boolean; isAvailable?: boolean }) => css`
    position: absolute;
    top: 6px;
    left: 8px;
    width: 100%;
    height: 100%;
    user-select: none;
    font: 12px ${Fonts.sansBody};
    text-shadow: 0px 2px black;
    color: ${props.isSelected ? Colors.white : Colors.midway};
    text-decoration: ${props.isAvailable ? 'none' : 'line-through double'};
  `};
`

export const SpaceBetweenFlexbox = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const PreWrapContainer = styled.div`
  white-space: pre-wrap;
`
